import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid
} from 'recharts';
import config from '../../config';

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
       viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CURRENT_DATE = new Date();
const ALL_MODELS = '__ALL__';

const generateColorPalettes = (models) => {
  const actual = {};
  const predicted = {};
  const actualPalette = ['#000000', '#333333', '#555555', '#777777', '#8B0000', '#5C0000'];
  const predictedPalette = ['#999999', '#AAAAAA', '#CCCCCC', '#DDDDDD', '#D27979', '#A36666'];
  models.forEach((model, index) => {
    actual[model] = actualPalette[index % actualPalette.length];
    predicted[model] = predictedPalette[index % predictedPalette.length];
  });
  return { actualColors: actual, predictedColors: predicted };
};

const toISO = (d) => {
  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  return dd.toISOString().split('T')[0];
};
const monthKey = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short' });

const processTimeSeriesData = (raw, period, modelNames) => {
  if (!raw || modelNames.length === 0) return { chartData: [], bucketPartsData: {} };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Boundary for Actual vs Predicted (1st day of the current month)
  const startOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const buckets = [];
  if (period === 'WTD') {
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - startOfWeek.getDay());
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      buckets.push({
        label: day.toLocaleDateString('en-US', { weekday: 'short' }),
        bucketKey: toISO(day),
        dateObj: day,
      });
    }
  } else if (period === 'MTD') {
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      const day = new Date(today.getFullYear(), today.getMonth(), i);
      buckets.push({ label: String(i), bucketKey: toISO(day), dateObj: day });
    }
  } else {
    for (let m = 0; m < 12; m++) {
      const day = new Date(today.getFullYear(), m, 1);
      buckets.push({
        label: day.toLocaleDateString('en-US', { month: 'short' }),
        bucketKey: monthKey(day),
        dateObj: day
      });
    }
  }

  const totalsByBucket = {};
  const bucketPartsData = {};

  Object.keys(raw).forEach((dateStr) => {
    const entryDate = new Date(dateStr);
    entryDate.setHours(0, 0, 0, 0);

    const keyForTotals = (period === 'YTD') ? monthKey(entryDate) : toISO(entryDate);

    const bucketExists = buckets.some(b => b.bucketKey === keyForTotals);
    if (!bucketExists) return;

    if (!totalsByBucket[keyForTotals]) totalsByBucket[keyForTotals] = {};
    if (!bucketPartsData[keyForTotals]) bucketPartsData[keyForTotals] = {};

    const modelsForDate = raw[dateStr] || {};
    Object.keys(modelsForDate).forEach((model) => {
      const m = modelsForDate[model];

      if (!totalsByBucket[keyForTotals][model]) {
        totalsByBucket[keyForTotals][model] = { total_actual_claims: 0, total_predicted_claims: 0 };
      }
      totalsByBucket[keyForTotals][model].total_actual_claims += (m.total_actual_claims || 0);
      totalsByBucket[keyForTotals][model].total_predicted_claims += (m.total_predicted_claims || 0);

      if (!bucketPartsData[keyForTotals][model]) {
        bucketPartsData[keyForTotals][model] = { actual_parts: {}, predicted_parts: {} };
      }

      // Actuals are only aggregated if the entry date is before the start of the current month
      if (entryDate < startOfCurrentMonth) {
        Object.entries(m.actual_parts || {}).forEach(([part, count]) => {
          bucketPartsData[keyForTotals][model].actual_parts[part] =
            (bucketPartsData[keyForTotals][model].actual_parts[part] || 0) + (count || 0);
        });
      }

      Object.entries(m.predicted_parts || {}).forEach(([part, count]) => {
        bucketPartsData[keyForTotals][model].predicted_parts[part] =
          (bucketPartsData[keyForTotals][model].predicted_parts[part] || 0) + (count || 0);
      });
    });
  });

  const chartData = buckets.map(({ label, bucketKey, dateObj }) => {
    const row = { name: label, bucketKey, isFuture: false };
    const models = totalsByBucket[bucketKey] || {};

    modelNames.forEach((model) => {
      const actualKey = `${model}_actual`;
      const predictedKey = `${model}_predicted`;
      const totals = models[model] || null;

      if (period === 'YTD') {
        const nowMonthIndex = today.getMonth();
        const rowMonthIndex = dateObj.getMonth();
        const isFutureMonth = rowMonthIndex >= nowMonthIndex;
        if (isFutureMonth) row.isFuture = true;
        row[actualKey] = isFutureMonth ? 0 : (totals?.total_actual_claims || 0);
        row[predictedKey] = totals?.total_predicted_claims || 0;
      } else {
        const isFutureDay = new Date(dateObj) >= startOfCurrentMonth;
        if (isFutureDay) row.isFuture = true;
        row[actualKey] = isFutureDay ? 0 : (totals?.total_actual_claims || 0);
        row[predictedKey] = totals?.total_predicted_claims || 0;
      }
    });

    return row;
  });

  return { chartData, bucketPartsData };
};

const TimeSeriesTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-black p-4 border border-black rounded-none shadow-none">
      <p className="font-semibold text-white text-xs uppercase tracking-widest mb-2">{label}</p>
      {payload.map((pld, i) => (
        <div key={i} style={{ color: pld.fill }} className="text-[10px] uppercase tracking-widest">
          {`${pld.name}: ${Number(pld.value ?? 0).toLocaleString()}`}
        </div>
      ))}
      <div className="text-[8px] text-gray-400 mt-3 uppercase tracking-widest">Click a bar to see part-wise details.</div>
    </div>
  );
};

const PartsTable = ({ title, parts }) => {
  const rows = useMemo(() => {
    const arr = Object.entries(parts || {}).map(([name, count]) => ({ name, count: Number(count || 0) }));
    arr.sort((a, b) => b.count - a.count);
    return arr;
  }, [parts]);

  if (!rows.length) return (
    <div className="text-[10px] text-gray-500 uppercase tracking-widest p-3">No data</div>
  );

  return (
    <div className="rounded-none border border-[#E5E5E5] overflow-hidden">
      <div className="px-3 py-2 bg-[#F7F7F7] text-[10px] uppercase tracking-widest font-semibold text-black">{title}</div>
      <div className="max-h-64 overflow-auto">
        <table className="min-w-full text-[10px] uppercase tracking-widest">
          <thead className="bg-white sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 border-b border-[#E5E5E5]">Part</th>
              <th className="text-right px-3 py-2 border-b border-[#E5E5E5]">Qty</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className="odd:bg-white even:bg-[#F7F7F7]">
                <td className="px-3 py-2">{r.name}</td>
                <td className="px-3 py-2 text-right font-semibold">{r.count.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PartWiseClaimsModal = ({ open, onClose, header, modelName, actualParts, predictedParts }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-[95%] max-w-4xl rounded-none border border-[#E5E5E5] p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">{header}</div>
            <h3 className="text-sm font-light text-black uppercase tracking-widest">
              {modelName.replace(/_/g, ' ')} — Parts Breakdown
            </h3>
          </div>
          <button onClick={onClose}
                  className="p-2 rounded-none hover:bg-[#F7F7F7] transition text-black" aria-label="Close">
            <XIcon />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <PartsTable title="Actual Parts (till last month)" parts={actualParts} />
          <PartsTable title="Predicted Parts" parts={predictedParts} />
        </div>

        <div className="mt-6 text-[10px] text-gray-500 uppercase tracking-widest">
          Note: Actuals include data up to the end of last month only. Current and future dates/months will not show actual parts.
        </div>
      </div>
    </div>
  );
};

const MazdaClaimsDashboard = () => {
  const [dailyClaimsData, setDailyClaimsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState('YTD');
  const [selectedModel, setSelectedModel] = useState(ALL_MODELS);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalHeader, setModalHeader] = useState('');
  const [modalModel, setModalModel] = useState('');
  const [modalActual, setModalActual] = useState({});
  const [modalPredicted, setModalPredicted] = useState({});

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/forecast-claims/`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('API error'))))
      .then((json) => {
        const dataObj = json?.data || json?.Data || json?.result || {};
        setDailyClaimsData(dataObj);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load');
        setLoading(false);
      });
  }, []);

  const allModelNames = useMemo(() => {
    if (!dailyClaimsData) return [];
    const s = new Set();
    Object.values(dailyClaimsData).forEach((dateEntry) => {
      Object.keys(dateEntry || {}).forEach((m) => s.add(m));
    });
    return Array.from(s).sort();
  }, [dailyClaimsData]);

  useEffect(() => {
    if (selectedModel !== ALL_MODELS && !allModelNames.includes(selectedModel)) {
      setSelectedModel(ALL_MODELS);
    }
  }, [allModelNames, selectedModel]);

  const displayedModels = useMemo(
    () => (selectedModel === ALL_MODELS ? allModelNames : [selectedModel]),
    [selectedModel, allModelNames]
  );

  const { actualColors, predictedColors } = useMemo(
    () => generateColorPalettes(allModelNames),
    [allModelNames]
  );

  const { chartData, bucketPartsData } = useMemo(
    () => processTimeSeriesData(dailyClaimsData, selectedPeriod, displayedModels),
    [dailyClaimsData, selectedPeriod, displayedModels]
  );

  const periodLabels = {
    YTD: `Yearly model wise Claims Distribution (${CURRENT_DATE.getFullYear()})`,
    MTD: `Monthly model wise Claims Distribution (${CURRENT_DATE.toLocaleDateString('en-US', { month: 'long' })})`,
    WTD: 'Weekly model wise Claims Distribution',
  };
  const chartTitle = periodLabels[selectedPeriod];

  const handleBarClick = (entry, modelName, series) => {
    if (!entry || !entry.payload) return;

    const { bucketKey, name, isFuture } = entry.payload;
    const bucket = bucketPartsData?.[bucketKey] || {};
    const partsOfModel = bucket?.[modelName] || { actual_parts: {}, predicted_parts: {} };

    const actualPartsForModal = isFuture ? {} : (partsOfModel.actual_parts || {});
    const predictedPartsForModal = partsOfModel.predicted_parts || {};

    setModalHeader(`${selectedPeriod} • ${name} • ${series === 'actual' ? 'Actual' : 'Predicted'}`);
    setModalModel(modelName);
    setModalActual(actualPartsForModal);
    setModalPredicted(predictedPartsForModal);
    setModalOpen(true);
  };

  if (loading) return <div className="p-8 text-center text-xs uppercase tracking-widest">Loading...</div>;
  if (error) return <div className="p-8 text-center text-[#8B0000] text-xs uppercase tracking-widest">Error: {error}</div>;
  if (!dailyClaimsData) return <div className="p-8 text-center text-xs uppercase tracking-widest">No data available.</div>;

  return (
    <div className="bg-white p-6 rounded-none border border-[#E5E5E5] font-sans">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <h1 className="text-sm font-light text-black uppercase tracking-widest">{chartTitle}</h1>

        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="bg-white border border-[#E5E5E5] rounded-none py-2 px-4 text-xs uppercase tracking-widest focus:ring-black focus:border-black"
            aria-label="Select period"
          >
            <option value="YTD">YTD</option>
            <option value="MTD">MTD</option>
            <option value="WTD">WTD</option>
          </select>

          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="bg-white border border-[#E5E5E5] rounded-none py-2 px-4 text-xs uppercase tracking-widest focus:ring-black focus:border-black"
            aria-label="Select model"
          >
            <option value={ALL_MODELS}>All Models</option>
            {allModelNames.map((m) => (
              <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="w-full h-[560px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" />
            <XAxis dataKey="name" tick={{ fill: '#000000', fontSize: 10, fontFamily: "'Inter', sans-serif" }} axisLine={{ stroke: '#E5E5E5' }} tickLine={false} />
            <YAxis tick={{ fill: '#000000', fontSize: 10, fontFamily: "'Inter', sans-serif" }} axisLine={false} tickLine={false} />
            <Tooltip content={<TimeSeriesTooltip />} wrapperStyle={{ opacity: 1, zIndex:100 }} cursor={{ fill: '#F7F7F7' }} />
            <Legend wrapperStyle={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: '600' }} iconType="rect" />
            {displayedModels.map((model) => (
              <React.Fragment key={model}>
                <Bar
                  dataKey={`${model}_actual`}
                  name={`${model.replace(/_/g, ' ')} Actual`}
                  fill={actualColors[model]}
                  stackId="actual"
                  onClick={(entry) => handleBarClick(entry, model, 'actual')}
                  className="cursor-pointer"
                />
                <Bar
                  dataKey={`${model}_predicted`}
                  name={`${model.replace(/_/g, ' ')} Predicted`}
                  fill={predictedColors[model]}
                  stackId="predicted"
                  onClick={(entry) => handleBarClick(entry, model, 'predicted')}
                  className="cursor-pointer"
                />
              </React.Fragment>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <PartWiseClaimsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        header={modalHeader}
        modelName={modalModel}
        actualParts={modalActual}
        predictedParts={modalPredicted}
      />
    </div>
  );
};

export default MazdaClaimsDashboard;